import { useForm } from '@mantine/form';
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { onNextInventoryItem, onPrevInventoryItem } from '../Inventory/Inventory.hooks';
import {
  autoUpdateRuleOnTargetComparableChangesAtom,
  calculatedTargetPriceAtom,
  filtersDirtyAtom,
  filtersEmptyAtom,
  initiallyAutoPricedAtom,
  marketplaceListingsAtom,
  pendingListingUpdatesAtom,
  rejectPendingUpdatesByListingIdAtom,
  ruleDirtyAtom,
  ruleStateAtom,
  ruleStateResetAtom,
  seatingChartFiltersAtom,
  sectionSuggestionsAtom,
  selectedEventAtom,
  selectedMarketplaceEventAtom,
  selectedMarketplaceIdAtom,
  selectedMergedListingAtom,
  selectedMergedListingPriceAtom,
  targetComparableFloorPriceRuleAtom,
  targetComparablesAtom,
  transientGlobalStateAtom,
  updateListingsAtom,
} from '../../data/atoms';
import {
  BrokerNerdsBarkerCoreEnumsPlanType,
  BrokerNerdsBarkerCoreModelsAdministrativePrincipalSettings,
  BrokerNerdsBarkerCoreModelsPricingRule,
  BrokerNerdsBarkerCoreModelsPricingRuleBase,
  BrokerNerdsBarkerCoreModelsPricingSchedulePricerSettings,
  getApiPricingRulesRuleId,
  useGetApiAccountingPlansCurrent,
} from '../../api';
import { openConfirmModal } from '@mantine/modals';
import { Text } from '@mantine/core';
import { useDidUpdate, useDisclosure, useLocalStorage } from '@mantine/hooks';
import yasml from '@thirtytech/yasml';
import { useRuleState } from '../../data/Rule.state';
import dayjs from 'dayjs';
import { useGlobalState } from '../../data/Global.state';
import { useIsDrasticChange } from '../../utils/price-utils';
import { useDidUpdateDeep, useEffectDeep } from '../../utils/use-memo-weak';
import { Rule } from '../../types';
import { useListingState } from '../../data/Listing.state';
import { formatCurrency } from '../../utils/formatters';
import { TenantIdListingId } from '../../models/tenantIdListingId';
import { useLocation } from 'react-router-dom';
import { calculateTargetComparableFloorAndCeiling, getMarketplaceTargetComparables } from '../../utils/rule-utils';
import { auth } from '../../data/atoms.auth';
import { useFlag } from '@unleash/proxy-client-react';
import { DEFAULT_RULE } from '../../data/constants';
import { GetTargetComparablePrice } from '../MarketListings/marketListingsFilter';
import { shouldSyncTrailingFloorPrice } from './NowPricing.utils';

export const onSaveRuleAndPrice = (ruleParams: Partial<BrokerNerdsBarkerCoreModelsPricingRuleBase> = {}) =>
  new CustomEvent('onSaveRuleAndPrice', {
    detail: ruleParams,
  });
onSaveRuleAndPrice.type = 'onSaveRuleAndPrice';

const normalizeTrailingFloor = (trailingFloor: Rule['trailingFloor']) => {
  if (!trailingFloor) {
    return trailingFloor;
  }

  if (trailingFloor.typeId === 'Amount' && trailingFloor.value > 0) {
    return {
      ...trailingFloor,
      value: -trailingFloor.value,
    };
  }

  return trailingFloor;
};

const isTrailingFloorValid = (trailingFloor: Rule['trailingFloor']) => {
  if (!trailingFloor) {
    return false;
  }

  if (trailingFloor.typeId === 'Amount') {
    return trailingFloor.value <= 0;
  }

  return trailingFloor.value > 0 && trailingFloor.value <= 1;
};

function NowPricingState() {
  const store = useStore();
  const selectedListing = useAtomValue(selectedMergedListingAtom);
  const { data: currentPlans } = useGetApiAccountingPlansCurrent({
    query: {
      enabled: !!selectedListing?.tenantId,
      select(data) {
        return data.data;
      },
    },
  });
  const selectedEvent = useAtomValue(selectedEventAtom);
  const seatingChartFilters = useAtomValue(seatingChartFiltersAtom);
  const { tenants } = useGlobalState('tenants');
  const companySettings = useMemo(() => tenants?.find((t) => t.tenantId === selectedListing?.tenantId)?.settings ?? {}, [selectedListing?.tenantId, tenants]);
  const targetComparables = useAtomValue(targetComparablesAtom);
  const selectedMarketplaceEvent = useAtomValue(selectedMarketplaceEventAtom);
  const [pendingListingUpdates, setPendingListingUpdates] = useAtom(pendingListingUpdatesAtom);
  const initiallyAutoPriced = useAtomValue(initiallyAutoPricedAtom);
  const filtersDirty = useAtomValue(filtersDirtyAtom);
  const filtersEmpty = useAtomValue(filtersEmptyAtom);
  const ruleDirty = useAtomValue(ruleDirtyAtom);
  const [rule, setRule] = useAtom(ruleStateAtom);
  const ruleStateReset = useSetAtom(ruleStateResetAtom);
  const rejectPendingUpdatesByListingId = useSetAtom(rejectPendingUpdatesByListingIdAtom);
  const [isChangeConflict, setIsChangeConflict] = useState<BrokerNerdsBarkerCoreModelsPricingRule | null>();
  const [{ autoUpdateAutoPricedListPriceRuleId }, setTransientGlobalState] = useAtom(transientGlobalStateAtom);
  const selectedMarketplaceId = useAtomValue(selectedMarketplaceIdAtom);
  const currentSelectedListingPrice = useAtomValue(selectedMergedListingPriceAtom);
  const [autoUpdateRuleOnTargetComparableChanges, setAutoUpdateRuleOnTargetComparableChanges] = useAtom(autoUpdateRuleOnTargetComparableChangesAtom);
  const { principal } = useGlobalState();
  const settings = useMemo(() => principal?.settings ?? ({} as BrokerNerdsBarkerCoreModelsAdministrativePrincipalSettings), [principal?.settings]);
  const updateListing = useSetAtom(updateListingsAtom);
  const {
    isLoading: ruleLoading,
    isLoadingLive: isRuleLoadingLive,
    saveRule,
    isLocalRule,
    applyRuleToGroupedListings,
    disbandRule,
    addCurrentRuleToListing,
    processPendingUpdates,
    setFormKey,
    listingsByRuleId,
    saveRuleTemplate,
  } = useRuleState(
    'isLoading',
    'isLoadingLive',
    'saveRule',
    'isLocalRule',
    'applyRuleToGroupedListings',
    'disbandRule',
    'addCurrentRuleToListing',
    'processPendingUpdates',
    'setFormKey',
    'listingsByRuleId',
    'saveRuleTemplate',
  );
  const { updatePrice } = useListingState('updatePrice');
  // Precompute listingsByRuleId keys once into a Set so the membership check below is O(1)
  // instead of rebuilding + scanning the mapped array per pending update (was O(n*m)).
  // toString() yields a deterministic `${tenantId}|${listingId}` key, so Set.has is equivalent to the prior .includes.
  const listingsByRuleIdKeys = useMemo(() => new Set(listingsByRuleId?.map((y) => TenantIdListingId.create(y.tenantId, y.listingId).toString())), [listingsByRuleId]);
  const hasPendingUpdates =
    // Filter out inline price changes. Any rule changes will be covered by listingsByRuleId filter
    // .some short-circuits and is equivalent to the prior `.filter(...).length > 0`.
    useAtomValue(pendingListingUpdatesAtom).some((x) => listingsByRuleIdKeys.has(TenantIdListingId.create(x.tenantId, x.listingId).toString()) || x.property !== 'unitPrice');

  const { cancelPendingChanges } = useListingState('cancelPendingChanges');

  const calculatedPrice = useAtomValue(calculatedTargetPriceAtom);

  const form = useForm<
    Omit<BrokerNerdsBarkerCoreModelsPricingRuleBase, 'tenantId' | 'marketplaceId' | 'pointOfSaleEventId' | 'filters'> & {
      listPrice: number | '';
      isScheduled: boolean;
    } & Partial<BrokerNerdsBarkerCoreModelsPricingSchedulePricerSettings>
  >({
    validateInputOnChange: false,
    validateInputOnBlur: true,
    initialValues: {
      listPrice: rule.isAutoPriced && (filtersDirty || ruleDirty) ? calculatedPrice : selectedListing?.unitPrice || 0,
      isAutoPriced: rule.isAutoPriced || rule.automationTypeId === 'AutoPrice' || false,
      floorPrice: rule.floorPrice,
      ceilingPrice: rule.ceilingPrice,
      adjustmentValue: Number((rule.adjustmentValue || 0).toFixed(4)),
      numActive: rule.numActive ?? 1,
      adjustmentTypeId: rule.adjustmentTypeId || 'Amount',
      staggerByTypeId: rule.staggerByTypeId || 'Amount',
      staggerByValue: rule.staggerByValue || 0,
      numComparables: rule.numComparables || 1,
      isScheduled: rule.automationTypeId === 'SchedulePrice',
      intervalMinutes: rule.schedulePricerSettings?.intervalMinutes,
      expiresAt: rule.schedulePricerSettings?.expiresAt ? selectedListing?.event.localDateTime : undefined,
      softFloor: rule.softFloor,
      trailingFloor: normalizeTrailingFloor(rule.trailingFloor),
      pausedAt: rule.pausedAt,
      pausedBy: rule.pausedBy,
      warningNotes: rule.warningNotes,
    },
    transformValues: (values) => {
      if (values.isScheduled) {
        return {
          ...values,
          isAutoPriced: false,
          automationTypeId: 'SchedulePrice',
          schedulePricerSettings: {
            intervalMinutes: Number(values.intervalMinutes),
            expiresAt: values.expiresAt!,
          },
        } satisfies typeof values;
      }
      if (values.isAutoPriced) {
        return {
          ...values,
          automationTypeId: 'AutoPrice',
          schedulePricerSettings: undefined,
        } satisfies typeof values;
      }

      return { ...values, schedulePricerSettings: undefined } satisfies Partial<Rule>;
    },
    validate: {
      listPrice: (value) => (value === '' || (typeof value === 'number' && value < 0) ? 'List price must be zero or greater' : null),
      numComparables: (value) => (!value || value < 1 ? 'Number of comparables must be greater than 0' : null),
      floorPrice: (value, values) => {
        const trailingFloorEnabled = !!values.trailingFloor;
        if (values.isAutoPriced || values.isScheduled) {
          if (!trailingFloorEnabled && !value) {
            return 'Floor price is required and cannot be $0';
          }
          if (typeof value === 'number' && value >= (values?.ceilingPrice || Infinity)) {
            return 'Floor price must be less than ceiling price';
          }
          if (typeof value === 'number' && companySettings.pricerSettings?.minimumFloorPrice && value < companySettings.pricerSettings.minimumFloorPrice) {
            return `Floor price must be greater than company limit of $${companySettings.pricerSettings.minimumFloorPrice}`;
          }
          if (values.isScheduled && typeof values.floorPrice === 'number' && Number(values.listPrice) < Number(values.floorPrice)) {
            return 'List price must be greater than or equal to floor price';
          }
        }
        return null;
      },
      intervalMinutes: (value, values) => {
        if (values.isScheduled && !value) {
          return 'Interval is required';
        }
        if (values.isScheduled && value && value < 5) {
          return 'Interval must be at least 5 minutes';
        }
        return null;
      },
      expiresAt: (value, values) => {
        if (values.isScheduled && !value) {
          return 'Expires At is required';
        }
        return null;
      },
      // numActive: (value) => (typeof value === 'number' && value < 1 ? 'Number of active tickets must be greater than 0' : null),
      ceilingPrice: (value, values) => {
        if (values.floorPrice && value && value <= values.floorPrice) {
          return 'Ceiling price must be larger than floor price';
        }
        if (values.isScheduled && Number(values.listPrice) > Number(values.ceilingPrice)) {
          return 'List price must be lower than or equal to floor price';
        }

        return null;
      },
      softFloor: {
        value: (value, values) => {
          if (values.softFloor?.value) {
            if (values.softFloor.value < 0) {
              return 'Soft floor value cannot be negative';
            }
          }
          return null;
        },
        period: (value, values) => {
          if (values.softFloor?.period === '0.00:00:00') {
            return 'Soft floor interval is required';
          }
          return null;
        },
      },
      trailingFloor: {
        value: (_value, values) => {
          if (!values.trailingFloor) {
            return null;
          }

          if (values.trailingFloor.typeId === 'Amount' && values.trailingFloor.value > 0) {
            return 'Trailing floor value must be less than or equal to 0';
          }

          if (values.trailingFloor.typeId === 'Percentage' && (values.trailingFloor.value <= 0 || values.trailingFloor.value > 1)) {
            return 'Trailing floor percent must be greater than 0 and less than or equal to 1';
          }

          return null;
        },
      },
      adjustmentValue: (value, values) => {
        if (values.isScheduled && !value) {
          return 'Adjustment value must be greater than 0';
        }
        if (
          values.adjustmentTypeId === 'Percentage' &&
          (companySettings.pricerSettings?.maximumPercentageUnder ?? 0) > 0 &&
          typeof value === 'number' &&
          parseFloat((value - 1).toFixed(4)) < -(companySettings.pricerSettings?.maximumPercentageUnder ?? 0)
        ) {
          return `Adjustment value cannot be under -${(companySettings.pricerSettings?.maximumPercentageUnder ?? 0) * 100}%`;
        }
        if (
          values.adjustmentTypeId === 'Amount' &&
          (companySettings.pricerSettings?.maximumAmountUnder ?? 0) > 0 &&
          typeof value === 'number' &&
          value < -(companySettings.pricerSettings?.maximumAmountUnder ?? 0)
        ) {
          return `Adjustment value cannot be under -${formatCurrency(companySettings.pricerSettings?.maximumAmountUnder ?? 0)}`;
        }
        return null;
      },
    },
  });
  const { values: formValues, isDirty: formIsDirty, isValid: formIsValid, reset: formReset, setFieldValue, resetDirty } = form;
  const [manualLoading, setManualLoading] = useState(false);
  const isLoading = isRuleLoadingLive || manualLoading;
  const isRuleRoute = useLocation().pathname.includes('/rule');
  const isFormReady = (isRuleRoute && !ruleLoading) || (!!(selectedListing && !ruleLoading) && selectedListing?.quantityReserved === 0);
  const isGroupedListing = (selectedListing?.ruleCount || 0) > 1;
  const formDirty = formIsDirty();
  const formValid = formIsValid();
  const isManualPricingOnlyPlan = useMemo(
    () =>
      !!selectedListing?.tenantId &&
      currentPlans?.some(
        (tenantPlan) => tenantPlan.tenantId === selectedListing.tenantId && tenantPlan.plan.planTypeId === BrokerNerdsBarkerCoreEnumsPlanType.ManualPricingOnly,
      ) === true,
    [currentPlans, selectedListing?.tenantId],
  );

  const principalSettings = useAtomValue(auth.principalAtom)?.settings;
  const targetComparablePrice = useMemo(() => GetTargetComparablePrice(targetComparables), [targetComparables]);
  const calculateFloorPriceFromTrailingFloor = useCallback((trailingFloor: Rule['trailingFloor'], comparablePrice: number | null | undefined) => {
    if (!trailingFloor || comparablePrice == null) {
      return undefined;
    }

    const calculatedFloorPrice = trailingFloor.typeId === 'Percentage' ? comparablePrice * trailingFloor.value : comparablePrice + trailingFloor.value;

    return Math.max(0, parseFloat(calculatedFloorPrice.toFixed(2)));
  }, []);
  const canSyncTrailingFloorPrice = shouldSyncTrailingFloorPrice({
    hasTrailingFloor: !!formValues.trailingFloor,
    isLocalRule,
    filtersDirty,
    formValues,
    isFieldDirty: form.isDirty,
  });
  const minFloorPrice = useAtomValue(auth.tenantsAtom).find((x) => x.tenantId === selectedListing?.tenantId)?.settings?.pricerSettings?.minimumFloorPrice || 0;
  const suggestions = useAtomValue(sectionSuggestionsAtom);
  const isSuggestionsAvailable =
    selectedListing && suggestions.tenantIdListingId?.toStringTyped() === selectedListing.tenantIdListingId && suggestions.suggestedSections.length > 0;
  const marketListings = useAtomValue(marketplaceListingsAtom);
  const easyButtonFlag = useFlag('easy-button');
  const [isEasyButtonAvailable, setIsEasyButtonAvailable] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const computeEasyButton = async () => {
      if (!easyButtonFlag || rule.isAutoPriced || !(selectedListing && isSuggestionsAvailable)) {
        return false;
      }
      const comparables = await getMarketplaceTargetComparables({
        outliers: [],
        marketplaceListings: marketListings,
        numComparables: rule.numComparables || 1,
        filters: {
          ...rule.filters,
          sectionIds: suggestions.suggestedSections,
          sectionRows: undefined,
        },
      });
      const targetPrices = calculateTargetComparableFloorAndCeiling({
        listing: selectedListing,
        minFloorPrice,
        settings: principalSettings,
        targetComparables: comparables,
      });
      return !!targetPrices.floorPrice;
    };
    computeEasyButton().then((result) => {
      if (!cancelled) {
        setIsEasyButtonAvailable(result);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    easyButtonFlag,
    isSuggestionsAvailable,
    marketListings,
    minFloorPrice,
    principalSettings,
    rule.filters,
    rule.isAutoPriced,
    rule.numComparables,
    selectedListing,
    suggestions.suggestedSections,
  ]);

  useEffect(() => {
    if (autoUpdateAutoPricedListPriceRuleId === rule?.ruleId) {
      if (rule.automationTypeId !== 'SchedulePrice') {
        form.setFieldValue('isAutoPriced', true);
        setRule({ ...rule, isAutoPriced: true });
        // Associate the template rule with the listing (sets selectedListing.ruleId + a pending
        // ruleId update). Without this, applyRuleToGroupedListings bails on its
        // `selectedListing.ruleId !== ruleState.ruleId` guard, so the auto-priced list price shows
        // in the UI but never gets a pending unitPrice update and is never saved.
        addCurrentRuleToListing();
      }
      setTransientGlobalState((prev) => ({ ...prev, autoUpdateAutoPricedListPriceRuleId: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoUpdateAutoPricedListPriceRuleId, formValues.isAutoPriced]);

  useEffect(() => {
    if (!isRuleLoadingLive && rule.isAutoPriced && !rule.pausedAt && formValid && (formDirty || filtersDirty)) {
      if (calculatedPrice && calculatedPrice !== formValues.listPrice) {
        setFieldValue('listPrice', calculatedPrice);
      }
    }
  }, [calculatedPrice, rule.isAutoPriced, filtersDirty, formValid, isRuleLoadingLive, formDirty, setFieldValue, rule.automationTypeId, formValues.listPrice, rule.pausedAt]);

  useDidUpdate(() => {
    if (currentSelectedListingPrice && !rule.isAutoPriced && formValues.listPrice !== currentSelectedListingPrice && formValid) {
      form.resetDirty({ ...formValues, listPrice: currentSelectedListingPrice });
      form.setFieldValue('listPrice', currentSelectedListingPrice);
    }
  }, [currentSelectedListingPrice]);

  const formValuesWithoutListPrice = useMemo(() => {
    const { listPrice: _listPrice, ...rest } = formValues;
    return rest;
  }, [formValues]);

  useDidUpdateDeep(
    () => {
      setRule((prev) => ({ ...prev, ...formValuesWithoutListPrice }));
    },
    [formValuesWithoutListPrice],
    [setRule],
  );

  useEffect(() => {
    if (!formValues.trailingFloor || !canSyncTrailingFloorPrice) {
      return;
    }

    const calculatedTrailingFloorPrice = calculateFloorPriceFromTrailingFloor(formValues.trailingFloor, targetComparablePrice);

    if (formValues.floorPrice !== calculatedTrailingFloorPrice) {
      form.setFieldValue('floorPrice', calculatedTrailingFloorPrice);
    }
  }, [calculateFloorPriceFromTrailingFloor, canSyncTrailingFloorPrice, form, formValues.floorPrice, formValues.trailingFloor, targetComparablePrice]);

  const [saving, setSaving] = useState(false);

  useEffectDeep(
    () => {
      if (!isRuleLoadingLive && (formDirty || ruleDirty) && (formValues.isAutoPriced || formValues.isScheduled) && !formValues.pausedAt && !saving && isGroupedListing) {
        applyRuleToGroupedListings(formValues.isScheduled ? formValues.listPrice || 0 : undefined);
      } else if (!isRuleLoadingLive && (formDirty || ruleDirty) && formValues.isScheduled && !saving && !isGroupedListing) {
        applyRuleToGroupedListings(formValues.listPrice || 0);
      } else if ((formDirty || ruleDirty) && !isGroupedListing && formValues.isAutoPriced) {
        applyRuleToGroupedListings();
      }
    },
    [formValues, rule.filters],
    [applyRuleToGroupedListings, formValues.isAutoPriced, formDirty, ruleDirty, isGroupedListing, isRuleLoadingLive],
  );

  const saveTemplate = useCallback(async () => {
    if (selectedListing) {
      await saveRuleTemplate({
        ...rule,
        marketplaceId: selectedMarketplaceEvent.marketplaceId!,
        pointOfSaleEventId: selectedListing.eventId,
        filters: {
          ...seatingChartFilters,
          noFilters: false,
        },
      });
    }
  }, [rule, saveRuleTemplate, seatingChartFilters, selectedListing, selectedMarketplaceEvent.marketplaceId]);

  const _saveRuleAndPrice = useCallback(
    async (values: typeof form.values) => {
      setManualLoading(true);

      if (values.softFloor && values.softFloor.value <= 0) {
        // eslint-disable-next-line no-param-reassign
        values.softFloor = undefined; // If soft floor is 0, remove it, the backend is expecting soft floor to be null if there's no valid value
      }

      if (values.trailingFloor && !isTrailingFloorValid(values.trailingFloor)) {
        // eslint-disable-next-line no-param-reassign
        values.trailingFloor = undefined; // If trailing floor is not valid, remove it before save.
      }

      if (values.trailingFloor) {
        // eslint-disable-next-line no-param-reassign
        values.softFloor = undefined;
        // eslint-disable-next-line no-param-reassign
        values.floorPrice = calculateFloorPriceFromTrailingFloor(values.trailingFloor, targetComparablePrice);
      }

      if (selectedListing) {
        if ((values.isAutoPriced || values.isScheduled) && !isLocalRule) {
          const { data: ruleDiff } = await getApiPricingRulesRuleId(rule.ruleId!, {
            headers: {
              'x-tenant-id': selectedListing.tenantId,
            },
          });
          if (dayjs(ruleDiff.updatedAt).diff(rule.updatedAt) !== 0 && !isRuleRoute) {
            setIsChangeConflict(ruleDiff);
            setManualLoading(false);
            return;
          }
        }

        setAutoUpdateRuleOnTargetComparableChanges(false);
        setSaving(true);

        try {
          if (filtersDirty || ruleDirty || values.isScheduled) {
            await saveRule({
              ...rule!,
              ...values,
              marketplaceId: selectedMarketplaceEvent.marketplaceId!,
              pointOfSaleEventId: selectedListing.eventId,
              filters: {
                ...seatingChartFilters,
                noFilters: false,
                ...(values.easyButtonUsed ? { sectionIds: suggestions.suggestedSections } : {}),
              },
            });
          } else {
            // Filter out inline price changes. Any rule changes will be covered by listingsByRuleId filter
            // Build the listingId Set once (O(1) membership) instead of rebuilding/scanning the mapped array per update; equivalent to the prior .includes.
            const ruleListingIds = new Set(listingsByRuleId?.map((y) => y.listingId));
            await processPendingUpdates(pendingListingUpdates.filter((x) => ruleListingIds.has(x.listingId) || !x.inlinePriceChange));
          }

          if (((!values.isAutoPriced && !values.isScheduled) || values.pausedAt) && values.listPrice !== selectedListing.unitPrice && typeof values.listPrice === 'number') {
            await updatePrice(selectedListing!.tenantId, selectedListing!.listingId, values.listPrice, selectedListing.unitPrice, true);
          }
        } catch {
          setSaving(false);
        }

        setManualLoading(false);
        setFormKey((x) => x + 1);
      }

      if (isRuleRoute && !selectedListing && selectedEvent && selectedMarketplaceEvent.marketplaceId) {
        await saveRule({
          ...rule!,
          ...values,
          marketplaceId: selectedMarketplaceEvent.marketplaceId,
          pointOfSaleEventId: selectedEvent.eventId,
          filters: {
            ...seatingChartFilters,
            noFilters: false,
          },
        });
        setFormKey((x) => x + 1);
      }
    },
    [
      filtersDirty,
      isLocalRule,
      isRuleRoute,
      calculateFloorPriceFromTrailingFloor,
      listingsByRuleId,
      pendingListingUpdates,
      processPendingUpdates,
      rule,
      ruleDirty,
      saveRule,
      seatingChartFilters,
      selectedEvent,
      selectedListing,
      selectedMarketplaceEvent.marketplaceId,
      setAutoUpdateRuleOnTargetComparableChanges,
      setFormKey,
      suggestions.suggestedSections,
      targetComparablePrice,
      updatePrice,
    ],
  );

  const saveRuleAndPrice = useCallback(
    (overrides?: Partial<typeof form.values>) =>
      form.onSubmit((_formValues) => {
        if (autoSaveDataRef.current) {
          setAutoSaveData(undefined);
          autoSaveDataRef.current = undefined;
        }
        _saveRuleAndPrice({ ..._formValues, ...overrides });
      })(),
    [form, _saveRuleAndPrice],
  );

  const [autoSaveData, setAutoSaveData] = useState<Partial<BrokerNerdsBarkerCoreModelsPricingRuleBase>>();
  const autoSaveDataRef = useRef<Partial<BrokerNerdsBarkerCoreModelsPricingRuleBase> | undefined>(undefined);

  useEffect(() => {
    if (autoSaveData) {
      autoSaveDataRef.current = autoSaveData;
      setTimeout(() => {
        saveRuleAndPrice(autoSaveData);
      }, 1000);
    }
  }, [autoSaveData, saveRuleAndPrice]);

  const saveRuleAndPriceEventHandler = useCallback(
    (e: Event) => {
      const customEvent = e as CustomEvent<Partial<BrokerNerdsBarkerCoreModelsPricingRuleBase>>;
      const overrides = customEvent.detail;
      // get the current values instead of waiting on rerender
      const target = store.get(targetComparableFloorPriceRuleAtom);
      if (overrides.easyButtonUsed && target) {
        overrides.floorPrice = target.floorPrice;
        overrides.ceilingPrice = target.ceilingPrice;
      }

      form.setValues(overrides);
      setTimeout(() => {
        const validation = form.validate();
        if (!validation.hasErrors) {
          saveRuleAndPrice({ ...form.getValues(), ...overrides });
        }
      }, 750);
    },
    [form, saveRuleAndPrice, store],
  );

  useEffect(() => {
    document.addEventListener(onSaveRuleAndPrice.type, saveRuleAndPriceEventHandler);
    return () => {
      document.removeEventListener(onSaveRuleAndPrice.type, saveRuleAndPriceEventHandler);
    };
  }, [saveRuleAndPriceEventHandler]);

  const displayConfirmationDialog = useCallback(
    () =>
      new Promise((resolve) => {
        openConfirmModal({
          title: 'Disable Auto-Pricer?',
          children: <Text size="sm">Are you sure you want to disable the auto-pricer for this listing? This will also disband the group.</Text>,
          labels: { confirm: 'Yes', cancel: 'No' },
          confirmProps: { className: 'confirmButton', variant: 'filled', color: 'gray', size: 'sm' },
          cancelProps: { className: 'cancelButton', variant: 'default', size: 'sm' },
          closeButtonProps: { size: 'md' },
          onAbort: () => resolve(false),
          onConfirm: async () => {
            setFieldValue('isAutoPriced', false);
            setFieldValue('listPrice', selectedListing!.unitPrice);
            resetDirty({
              isAutoPriced: false,
              listPrice: selectedListing!.unitPrice,
              isScheduled: false,
            });
            disbandRule();
            resolve(true);
          },
        });
      }),
    [setFieldValue, selectedListing, resetDirty, disbandRule],
  );

  useDidUpdate(() => {
    // Handles condition where auto price is automatically enabled by drag and drop but wouldn't have been abled by default.
    if (formValues.isAutoPriced && formValues.floorPrice == null) {
      setAutoUpdateRuleOnTargetComparableChanges(true);
    }
  }, [formValues.isAutoPriced]);

  const targetComparableFloorPriceRule = useAtomValue(targetComparableFloorPriceRuleAtom);

  useEffect(() => {
    if (autoUpdateRuleOnTargetComparableChanges && !form.values.isScheduled && !form.values.trailingFloor) {
      form.setFieldValue('floorPrice', targetComparableFloorPriceRule.floorPrice);
      form.setFieldValue('ceilingPrice', targetComparableFloorPriceRule.ceilingPrice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetComparables, autoUpdateRuleOnTargetComparableChanges]);

  const onSchedulePricerToggle = useCallback(() => {
    if (isManualPricingOnlyPlan) {
      return;
    }

    if (!formValues.isScheduled) {
      // setFieldValue('floorPrice', selectedListing!.unitCost * 1.1);
      // setFieldValue('intervalMinutes', 60);
      if (selectedListing?.event.localDateTime) {
        setFieldValue('expiresAt', dayjs(selectedListing?.event.localDateTime).add(1, 'day').toDate());
      }
      setRule({ ...rule, isAutoPriced: false, automationTypeId: 'SchedulePrice' }, { enableAutoPricing: 'Scheduled' });
      if (selectedListing && !selectedListing?.ruleId) {
        setPendingListingUpdates((list) => [
          ...list,
          {
            listingId: selectedListing?.listingId!,
            tenantId: selectedListing?.tenantId!,
            property: 'ruleId',
            previousValue: selectedListing?.ruleId,
            value: rule.ruleId,
          },
        ]);
        selectedListing.ruleId = rule.ruleId;
        updateListing(selectedListing);
      }
      setPendingListingUpdates((list) => [
        ...list,
        {
          listingId: selectedListing?.listingId!,
          tenantId: selectedListing?.tenantId!,
          property: 'pricerStatusId',
          previousValue: selectedListing?.pricerStatusId!,
          value: 'Scheduled',
        },
      ]);
      setFormKey((x) => x + 1);
      if (isGroupedListing) {
        // Note: Use unitPrice because formValues.listPrice is still manipulated by the rule
        applyRuleToGroupedListings(selectedListing?.unitPrice || 0);
      }
    } else {
      // Note: This doesn't run anymore but keeping it around for a bit
      setFieldValue('expiresAt', undefined);
      setFieldValue('intervalMinutes', undefined);
      setFieldValue('floorPrice', undefined);
      setFieldValue('adjustmentValue', undefined);
      setRule((r) => ({ ...r, isAutoPriced: false, automationTypeId: 'None', schedulePricerSettings: undefined }));
      setFormKey((x) => x + 1);
    }
  }, [
    applyRuleToGroupedListings,
    formValues.isScheduled,
    isGroupedListing,
    isManualPricingOnlyPlan,
    rule,
    selectedListing,
    setFieldValue,
    setFormKey,
    setPendingListingUpdates,
    setRule,
    updateListing,
  ]);

  const onAutoPricerToggle = useCallback(
    async (e: SyntheticEvent<HTMLInputElement, Event> | boolean) => {
      const checked = typeof e !== 'boolean' ? (e.target as HTMLInputElement).checked : e;
      if (checked && isManualPricingOnlyPlan) {
        return;
      }

      if (!checked && isGroupedListing) {
        displayConfirmationDialog();
      } else if (!checked && isLocalRule) {
        setFieldValue('isAutoPriced', checked);
        setFieldValue('isScheduled', false);
        setFieldValue('listPrice', selectedListing?.unitPrice || 0);
        setFieldValue('pausedAt', null);
        setFieldValue('pausedBy', null);
        rejectPendingUpdatesByListingId(selectedListing?.tenantIdListingId!);
        setRule(null);
        setRule({ ...rule, pausedAt: null, pausedBy: null, isAutoPriced: false, automationTypeId: 'None', filters: { ...rule.filters } });
      } else if (!checked && !isLocalRule && !isGroupedListing) {
        setFieldValue('isAutoPriced', false);
        setFieldValue('isScheduled', false);
        setFieldValue('listPrice', selectedListing!.unitPrice);
        setFieldValue('pausedAt', null);
        setFieldValue('pausedBy', null);
        rejectPendingUpdatesByListingId(selectedListing?.tenantIdListingId!);
        setRule({ ...rule, pausedAt: null, pausedBy: null, isAutoPriced: false, automationTypeId: 'None' });
      } else {
        setFieldValue('isAutoPriced', true);
        setFieldValue('isScheduled', false);
        setFieldValue('listPrice', selectedListing!.unitPrice);
        setFieldValue('numComparables', settings.pricerSettings?.numComparables?.[0]?.numComparables ?? DEFAULT_RULE.numComparables);
        setFieldValue('staggerByTypeId', settings.pricerSettings?.staggerByTypeId ?? DEFAULT_RULE.staggerByTypeId);
        setFieldValue('staggerByValue', settings.pricerSettings?.staggerByValue ?? DEFAULT_RULE.staggerByValue);
        setFieldValue('adjustmentTypeId', settings.pricerSettings?.adjustmentTypeId ?? DEFAULT_RULE.adjustmentTypeId);
        setFieldValue('adjustmentValue', settings.pricerSettings?.adjustmentValue ?? DEFAULT_RULE.adjustmentValue);
        setFieldValue('numActive', settings.pricerSettings?.numActive ?? DEFAULT_RULE.numActive);
        setFieldValue('pausedAt', null);
        setFieldValue('pausedBy', null);
        setAutoUpdateRuleOnTargetComparableChanges(true);
        setRule(
          {
            ...rule,
            pausedAt: null,
            pausedBy: null,
            isAutoPriced: checked,
            automationTypeId: 'AutoPrice',
            numComparables: settings.pricerSettings?.numComparables?.[0]?.numComparables ?? DEFAULT_RULE.numComparables,
            numActive: settings.pricerSettings?.numActive ?? DEFAULT_RULE.numActive,
            staggerByTypeId: settings.pricerSettings?.staggerByTypeId ?? DEFAULT_RULE.staggerByTypeId,
            staggerByValue: settings.pricerSettings?.staggerByValue ?? DEFAULT_RULE.staggerByValue,
            adjustmentTypeId: settings.pricerSettings?.adjustmentTypeId ?? DEFAULT_RULE.adjustmentTypeId,
            adjustmentValue: settings.pricerSettings?.adjustmentValue ?? DEFAULT_RULE.adjustmentValue,
          },
          { enableAutoPricing: 'Auto-Pricing' },
        );
        addCurrentRuleToListing();
        if (formValues.isScheduled && isGroupedListing) {
          // Figure out what happens when there is no target comparables. Old pending updates stick around.
          applyRuleToGroupedListings();
        }
      }
      setFormKey((x) => x + 1);
    },
    [
      addCurrentRuleToListing,
      applyRuleToGroupedListings,
      displayConfirmationDialog,
      formValues.isScheduled,
      isGroupedListing,
      isManualPricingOnlyPlan,
      isLocalRule,
      rejectPendingUpdatesByListingId,
      rule,
      selectedListing,
      setAutoUpdateRuleOnTargetComparableChanges,
      setFieldValue,
      setFormKey,
      setRule,
      settings.pricerSettings?.adjustmentTypeId,
      settings.pricerSettings?.adjustmentValue,
      settings.pricerSettings?.numActive,
      settings.pricerSettings?.numComparables,
      settings.pricerSettings?.staggerByTypeId,
      settings.pricerSettings?.staggerByValue,
    ],
  );

  const cancel = useCallback(async () => {
    if (isGroupedListing && isLocalRule) {
      const result = await displayConfirmationDialog();
      if (!result) {
        return;
      }
    }
    formReset();
    ruleStateReset({ retainFilters: !rule.isAutoPriced });
    cancelPendingChanges(
      // Filter out inline price changes. Any rule changes will be covered by listingsByRuleId filter
      listingsByRuleId
        ?.map((x) => TenantIdListingId.create(x.tenantId, x.listingId).toStringTyped())
        .concat(pendingListingUpdates.filter((x) => !x.inlinePriceChange).map((x) => TenantIdListingId.create(x.tenantId, x.listingId).toStringTyped())),
    );
    setFormKey((x) => x + 1);
  }, [
    cancelPendingChanges,
    displayConfirmationDialog,
    formReset,
    isGroupedListing,
    isLocalRule,
    listingsByRuleId,
    pendingListingUpdates,
    rule.isAutoPriced,
    ruleStateReset,
    setFormKey,
  ]);

  const nextListing = useCallback(async () => {
    document.dispatchEvent(onNextInventoryItem);
  }, []);
  const prevListing = useCallback(async () => {
    document.dispatchEvent(onPrevInventoryItem);
  }, []);

  const isDrasticChange = useIsDrasticChange();

  const _isDrasticChange = useMemo(
    () => typeof formValues.listPrice === 'number' && isDrasticChange(formValues.listPrice, selectedListing?.unitPrice!, selectedListing?.unitCost!),
    [formValues.listPrice, isDrasticChange, selectedListing?.unitCost, selectedListing?.unitPrice],
  );

  const hasSectionRows = [...(rule.filters.sectionRows?.values() ?? [])]?.some((x) => x.sectionId !== '');

  const isNoSectionRule = useMemo(
    () => rule.isAutoPriced && rule?.filters?.sectionIds?.length === 0 && !hasSectionRows,
    [hasSectionRows, rule?.filters?.sectionIds?.length, rule.isAutoPriced],
  );

  const refreshRule = useCallback(async () => {
    const { data: updatedRule } = await getApiPricingRulesRuleId(rule.ruleId!, {
      headers: {
        'x-tenant-id': selectedListing?.tenantId,
      },
    });
    setRule(updatedRule, { forceUpdate: true, currentListingId: selectedListing?.tenantIdListingId });
    setIsChangeConflict(null);
    setFormKey((x) => x + 1);
  }, [rule.ruleId, selectedListing?.tenantId, selectedListing?.tenantIdListingId, setRule, setFormKey]);

  const [isDrasticChangeSnoozed, setIsDrasticChangeSnoozed] = useLocalStorage<Date | undefined>({
    key: 'drasticChangeSnooze',
  });

  const [isDrasticChangeDialog, { open: openDrasticChangeDialog, close: closeDrasticChangeDialog }] = useDisclosure();
  const [isNoSectionDialog, { open: openNoSectionDialog, close: closeNoSectionDialog }] = useDisclosure(false);
  const onSave = form.onSubmit((values) => {
    if (!values.isAutoPriced && _isDrasticChange && dayjs(isDrasticChangeSnoozed).diff(dayjs()) <= 0) {
      openDrasticChangeDialog();
    } else if (isNoSectionRule) {
      openNoSectionDialog();
    } else {
      saveRuleAndPrice();
    }
  });

  return {
    form,
    selectedListing,
    saveRuleAndPrice,
    isLoading,
    isGroupedListing,
    isFormReady,
    nextListing,
    prevListing,
    onAutoPricerToggle,
    cancel,
    filtersDirty,
    filtersEmpty,
    ruleDirty,
    selectedMarketplaceEvent,
    hasPendingUpdates,
    initiallyAutoPriced,
    isDrasticChange: _isDrasticChange,
    isChangeConflict,
    refreshRule,
    setAutoUpdateRuleOnTargetComparableChanges,
    selectedMarketplaceId,
    isManualPricingOnlyPlan,
    isNoSectionRule,
    onSchedulePricerToggle,
    isDirty: ruleDirty || filtersDirty,
    openDrasticChangeDialog,
    closeDrasticChangeDialog,
    openNoSectionDialog,
    closeNoSectionDialog,
    onSave,
    isNoSectionDialog,
    isDrasticChangeDialog,
    setIsDrasticChangeSnoozed,
    saving,
    isEasyButtonAvailable,
    saveTemplate,
    suggestions,
    targetComparablePrice,
  };
}

export const { Provider: NowPricingProvider, useSelector: useNowPricing } = yasml(NowPricingState);
